import { useEffect, useRef, useState } from 'react';
import styles from './Tags.module.css';
import { API } from '../../services';
import { useAuthenticate } from '../../hooks';
import {
    setToastComponent,
    showSuccessToast,
} from '../../services/setup/ApiResponseHandler';
import { useIntl } from 'react-intl';
import AIEduInputText from '../../components/Input/AIEduInputText';
import AIEduPassword from '../../components/Input/AIEduPassword';
import { copyToClipboard } from '../../utils/Utils';
import AIEduSpinner from '../../components/Spinner/AIEduSpinner';
import { useNavigate, useParams } from 'react-router-dom';
import { TAG_MANAGEMENT_PATH } from '../../router/RouteConstants';
import AIEduToast from '../../components/Toast/AIEduToast';
import AIEduDialog from '../../components/Dialog/AIEduDialog';
import AIEduButton from '../../components/Button/AIEduButton';

const EditTag = () => {
    const intl = useIntl();
    const { formatMessage: f } = intl;
    const { tagId } = useParams();
    const navigate = useNavigate();

    const { accountInfo } = useAuthenticate();
    const { organizations, place } = accountInfo ?? {};
    const { placeId } = place ?? {};
    const { organizationId } = organizations?.[0] ?? {};

    const [tag, setTag] = useState({});

    const [loading, setLoading] = useState(false);
    const [visible, setVisible] = useState(false);
    const toastRef = useRef(null);

    const getTagDetail = async () => {
        setLoading(true);
        const resp = await API.getTagById(organizationId, placeId, tagId);
        if (resp.status === 200) {
            setTag(resp?.data?.data);
        }
        setLoading(false);
    };

    const handleApiKeyRegeneration = async () => {
        const resp = await API.regenerateKey(organizationId, placeId, tagId);
        if (resp.status === 201) {
            getTagDetail();
            showSuccessToast('API Key Regenerated successfully');
        }
        setVisible(false);
    };

    useEffect(() => {
        setToastComponent(toastRef.current);
    }, []);

    useEffect(() => {
        getTagDetail();
    }, [tagId]);

    const navigateToTags = () => {
        navigate(TAG_MANAGEMENT_PATH);
    };

    const copyWebhookUrl = () => {
        copyToClipboard(tag?.webHookUrl);
        showSuccessToast('Copied');
    };

    const copyApiKey = () => {
        copyToClipboard(tag?.password);
        showSuccessToast('Copied');
    };

    const hideRegenerateKeyDialog = () => {
        setVisible(false);
    };

    return (
        <>
            {loading && <AIEduSpinner />}
            <AIEduToast ref={toastRef} />
            <div className={styles.tags}>
                <div className={styles.edit_tags_header}>
                    <span
                        onClick={navigateToTags}
                        className='cursor-pointer hover:underline'
                    >
                        {f({ id: 'AI_EDU_TAG_MANAGEMENT' })}
                    </span>
                    <i className='pi pi-chevron-right mx-2' />
                    <span>{`${tag?.tagName}`}</span>
                </div>
                <div className={`p-5 ${styles.tagInfo}`}>
                    <div className='flex align-items-end gap-5 mb-4'>
                        <AIEduInputText
                            title='WebHook URL:'
                            value={tag?.webHookUrl}
                            disabled
                            width={'90%'}
                        />

                        <i
                            className='pi pi-clone cursor-pointer'
                            style={{ fontSize: '15px' }}
                            onClick={copyWebhookUrl}
                        />
                    </div>

                    <div className='flex align-items-end gap-5'>
                        <AIEduPassword
                            title='API Key:'
                            value={tag?.password}
                            toggleMask
                            feedback={false}
                            disabled
                            width={'90%'}
                        />

                        <div className='flex gap-5'>
                            <i
                                className='pi pi-clone cursor-pointer'
                                style={{ fontSize: '15px' }}
                                onClick={copyApiKey}
                            />

                            <i
                                className='pi pi-sync cursor-pointer'
                                style={{ fontSize: '15px' }}
                                onClick={() => setVisible(true)}
                            />
                        </div>
                    </div>
                </div>
                <div className='mt-4' style={{ fontFamily: 'SamsungOne-600' }}>
                    Instructions: <br />
                    You can quickly access the third-party platform via the link
                    below: <br />
                    InformaCast:{' '}
                    <a
                        href='https://admin.icmobile.singlewire.com/icmobile'
                        target='_blank'
                    >
                        https://admin.icmobile.singlewire.com/icmobile
                    </a>
                </div>
                <AIEduDialog
                    visible={visible}
                    style={{
                        width: '40rem',
                        fontFamily: 'SamsungOne-600, sans-serif',
                    }}
                    footer={
                        <div className='flex justify-content-end'>
                            <AIEduButton
                                label='Cancel'
                                className='btnSecondary'
                                onClick={hideRegenerateKeyDialog}
                            />
                            <AIEduButton
                                label='Confirm'
                                className='btnPrimary'
                                onClick={handleApiKeyRegeneration}
                            />
                        </div>
                    }
                    onHide={hideRegenerateKeyDialog}
                    content={
                        <>
                            Regenerating the corresponding API Key can avoid the
                            risk of information leakage. After the Key is
                            generated, you need to manually synchronize it to
                            the platform where the information is obtained. Are
                            you sure you want to regenerate it?
                        </>
                    }
                />
            </div>
        </>
    );
};

export default EditTag;
